import { createRequest } from "@/common/helpers/RequestHelpers";
import { Actions as PENSION_GROUP_ACTIONS } from "@/redux/actions/groupActions";
import { Actions as INTEGRATION_ACTIONS } from "@/redux/actions/integrationActions";
import { Actions as NOTIFICATION_ACTIONS } from "@/redux/actions/notificationActions";
import { Actions as PENSION_SCHEME_ACTIONS } from "@/redux/actions/schemeActions";
import { NotificationManager } from "react-notifications";
import { INTEGRATION_NAME, INTEGRATION_TYPE, INTEGRATION_VARIABLE } from "./config";

const LOCAL = false;

// import apiURL from "@/apiURL";
const Types = {
	SMART_PENSION_EMPLOYEE_LINK: "SMART_PENSION_EMPLOYEE_LINK",
	SMART_PENSION_GETTING_EMPLOYEES: "SMART_PENSION_GETTING_EMPLOYEES",
	SMART_PENSION_OPTIONS: "SMART_PENSION_OPTIONS",
	SMART_PENSION_SENDING_PAY_PERIOD: "SMART_PENSION_SENDING_PAY_PERIOD",
	SMART_PENSION_UPDATE_PAY_PERIOD: "SMART_PENSION_UPDATE_PAY_PERIOD",
	SMART_PENSION_ERRORS_MODAL: "SMART_PENSION_ERRORS_MODAL",
};

const request = createRequest({ urlPath: "/integrations/smartPension/", local: LOCAL });

const setOptions = (options) => ({
	type: Types.SMART_PENSION_OPTIONS,
	options,
});
const setEmployeeLink = (links, employees) => ({
	type: Types.SMART_PENSION_EMPLOYEE_LINK,
	links,
	employees,
});

const updatePayPeriod = (period) => ({
	type: Types.SMART_PENSION_UPDATE_PAY_PERIOD,
	period,
});

const gettingEmployeesLinks = (status) => ({
	type: Types.SMART_PENSION_GETTING_EMPLOYEES,
	status,
});

const toggleErrorsModal = (payPeriodId) => ({
	type: Types.SMART_PENSION_ERRORS_MODAL,
	payPeriodId,
});

const connectSmartPension = (type) => {
	return async (dispatch, getState) => {
		const url = window.location.href;

		await request({ method: "GET", path: `connect/${type}`, query: { url } })
			.then(({ data }) => {
				if (data.url === url) dispatch(INTEGRATION_ACTIONS.integrationGetList());
				else window.location.href = data.url;
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Connecting ${INTEGRATION_NAME}`, `Connect ${INTEGRATION_NAME}`);
			});
	};
};

const getSmartPensionOrganisations = () => {
	return async (dispatch, getState) => {
		dispatch(setOptions({ loadingOrganisations: true }));

		await request({ method: "GET", path: "organisations" }).then(({ data }) => {
			dispatch(setOptions({ organisations: data.organisations }));
		});

		dispatch(setOptions({ loadingOrganisations: undefined }));
	};
};

const updateSmartPensionConnectedOrg = ({ companyName, organisationId }) => {
	return async (dispatch, getState) => {
		const state = getState();
		const existingOrg = state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.connectionId;

		if (existingOrg === organisationId) return;

		dispatch(setOptions({ switchingOrganisation: true, groups: undefined, schemes: undefined }));

		await request({ method: "POST", path: "link-organisation", body: { companyName, organisationId } })
			.then(({ data }) => {
				if (!companyName) companyName = data.companyName;

				dispatch(setOptions({ connectionId: organisationId, companyName }));
				NotificationManager.success(
					`Successfully Linked to ${companyName}`,
					`Linking ${INTEGRATION_NAME} Company`,
				);
			})
			.catch((error) => {
				console.error(error);
				let message = error.response?.data?.error;
				NotificationManager.error(
					message ? message : `Error Linking Company`,
					`Linking ${INTEGRATION_NAME} Company`,
				);
			});

		dispatch(setOptions({ switchingOrganisation: undefined }));
	};
};

const importPensionSchemes = () => {
	return async (dispatch, getState) => {
		const state = getState();
		const connectionId = state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.connectionId;
		if (!connectionId) return;

		dispatch(setOptions({ importingSchemes: true }));

		await request({
			method: "POST",
			path: `import-schemes/${connectionId}`,
		})
			.then(({ data }) => {
				const { schemes, groups } = data;
				dispatch(PENSION_GROUP_ACTIONS.updateGroups(groups));
				dispatch(PENSION_SCHEME_ACTIONS.updateSchemes(schemes));
				NotificationManager.success(`Successfully Imported Pension Schemes`, `Importing Pension Schemes`);
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Importing Pension Schemes`, `Importing Pension Schemes`);
			});

		dispatch(setOptions({ importingSchemes: undefined }));
	};
};

const getSmartPensionSchemes = () => {
	return async (dispatch, getState) => {
		const state = getState();
		if (!state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.connectionId) return;

		dispatch(PENSION_SCHEME_ACTIONS.getSchemes());

		const { schemes, groups, pensionPayScheduleOptions } =
			(await request({ method: "GET", path: "schemes" }))?.data || {};

		dispatch(setOptions({ schemes, groups, pensionPayScheduleOptions }));
	};
};

const getSmartPensionSettings = () => {
	return async (dispatch, getState) => {
		await request({ method: "GET", path: "settings" });
	};
};

const saveSmartPensionSettings = (body) => {
	return (dispatch, getState) => {
		INTEGRATION_ACTIONS.integrationUpdateSettings(INTEGRATION_NAME, body)
			.then(() => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Saving ${INTEGRATION_NAME}`, `${INTEGRATION_NAME} Settings Saved`);
			});
	};
};

const sendingPayPeriod = (periodId, status) => ({
	type: Types.SMART_PENSION_SENDING_PAY_PERIOD,
	periodId,
	status,
});

const sendPayPeriodToSmartPension = (periodId) => {
	return async (dispatch, getState) => {
		const state = getState();

		const sending = state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.sendingPayroll || [];

		if (sending.includes(periodId)) return;

		dispatch(sendingPayPeriod(periodId, true));

		await request({ method: "POST", path: "payPeriod", body: { periodId } })
			.then(({ data }) => {
				switch (data.status) {
					case "success":
						NotificationManager.success(
							`Successfully Sent Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
					case "error":
						NotificationManager.error(
							`Error Sending Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
				}
				dispatch(updatePayPeriod(data));
			})
			.catch((error) => {
				let message;
				if (error.isAxiosError) {
					message = error.response?.data?.error;
				} else {
					console.error(error);
				}
				NotificationManager.error(
					message ? message : `Error Sending Pay Period to ${INTEGRATION_NAME}`,
					`Send Pay Period to ${INTEGRATION_NAME}`,
				);
			});

		dispatch(sendingPayPeriod(periodId));
	};
};

const getPayPeriodStatus = (periodId) => {
	return async (dispatch, getState) => {
		console.debug(periodId);
		await request({ method: "GET", path: `payPeriod/${periodId}` })
			.then(({ data }) => {
				console.debug(data.status);
				switch (data.status) {
					case "success":
						NotificationManager.success(
							`Successfully Sent Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
					case "error":
						NotificationManager.error(
							`Error Sending Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
				}
				dispatch(updatePayPeriod(data));
			})
			.catch((error) => {
				console.error(error);
			});
	};
};

const disconnectSmartPension = () => {
	return async (dispatch, getState) => {
		await request({ method: "DELETE", path: "revoke" })
			.then(() => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Disconnecting ${INTEGRATION_NAME}`, `Disconnect ${INTEGRATION_NAME}`);
			});
	};
};

const getOptOuts = (query) => {
	return async (dispatch, getState) => {
		console.debug(query);
		await request({ method: "GET", path: "opt-outs", query })
			.then(({ data }) => {
				// console.debug(data);
				if (data?.optOuts?.length > 0) NOTIFICATION_ACTIONS.getNotifications();
			})
			.catch((error) => {
				console.error(error);
			});
	};
};

const getSmartPensionEmployees = () => {
	return async (dispatch, getState) => {
		dispatch(setOptions({ loadingEmployees: true }));

		await request({ method: "GET", path: "employees" }).then(({ data }) => {
			const { links, employees } = data;
			dispatch(setOptions({ links, employees }));
		});

		dispatch(setOptions({ loadingEmployees: undefined }));
	};
};

const importSmartPensionEmployees = (employees) => {
	return async (dispatch, getState) => {
		await request({ method: "POST", path: "employees", body: { employees } }).then(({ data }) => {
			const { links } = data;
			dispatch(setOptions({ links }));
		});
	};
};

const linkEmployee = ({ agreement, referenceLink }) => {
	return async (dispatch, getState) => {
		if (referenceLink === "desynced") dispatch(setOptions({ desyncingEmployee: true }));
		const request = createRequest({ urlPath: "/integrations/Smart Pension/", local: LOCAL });

		await request({
			method: "POST",
			path: "link-employee",
			body: { agreementId: agreement, link: referenceLink },
		})
			.then(() => {
				dispatch(getSmartPensionEmployees());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(
					`Error Linking Employee with ${INTEGRATION_NAME}`,
					`Linking Employee with ${INTEGRATION_NAME}`,
				);
			});
		if (referenceLink === "desynced") dispatch(setOptions({ desyncingEmployee: undefined }));
	};
};

const resyncDetails = () => {
	return async (dispatch, getState) => {
		dispatch(setOptions({ resyncing: true }));

		await request({ method: "GET", path: "sync" }).then(() => {
			dispatch(NOTIFICATION_ACTIONS.getNotifications());
			NotificationManager.success(`Successfully Resynced ${INTEGRATION_NAME}`, `Resyncing ${INTEGRATION_NAME}`);
		});

		dispatch(setOptions({ resyncing: undefined }));
	};
};

const resyncEmployee = (agreement) => {
	return async (dispatch, getState) => {
		await request({ method: "GET", path: `sync/employees/${agreement}` }).then(() => {
			dispatch(NOTIFICATION_ACTIONS.getNotifications());
			NotificationManager.success(
				`Successfully Resynced Employee with ${INTEGRATION_NAME}`,
				`Resyncing Employee with ${INTEGRATION_NAME}`,
			);
		});
	};
};

const getCompanyList = () => {
	return async (dispatch, getState) => {
		dispatch(setOptions({ loadingOrganisations: true }));

		await request({ method: "GET", path: `companies` }).then(({ data }) => {
			const { companies } = data;
			dispatch(setOptions({ companies }));
		});

		dispatch(setOptions({ loadingOrganisations: undefined }));
	};
};

const Actions = {
	resyncDetails,
	linkEmployee,
	connectSmartPension,
	disconnectSmartPension,
	setEmployeeLink,
	updateSmartPensionConnectedOrg,
	gettingEmployeesLinks,
	saveSmartPensionSettings,
	getSmartPensionSettings,
	getSmartPensionSchemes,
	getSmartPensionOrganisations,
	importPensionSchemes,
	sendPayPeriodToSmartPension,
	getPayPeriodStatus,
	toggleErrorsModal,
	getOptOuts,
	getSmartPensionEmployees,
	importSmartPensionEmployees,
	resyncEmployee,
	getCompanyList,
};

export { Actions, Types };
